import React from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { styled, Tabs as TabsMui, TabsProps } from '@mui/material'
import mixpanel from 'mixpanel-browser'
import Link from 'next/link'

import Tab from 'components/Tab/Tab'

import getCurrentMonth from 'utils/get-current-month'
import { FeatureTabs } from 'utils/tabs'
import { ObjectLabelValue } from 'utils/types/object-label-value'

import { theme } from 'styles/theme'

interface TabsExtendsProps extends TabsProps {
  value: number | string
  tabs: Array<string> | Array<ObjectLabelValue>
  onChange: (event: React.SyntheticEvent, newValue: number | string) => void
  fontSize?: number
  hideBottomLine?: boolean
}
const mixpanelTyped = mixpanel as unknown as { __loaded: string }
const Tabs = styled(
  ({
    tabs,
    fontSize = 14,
    onChange,
    hideBottomLine,
    ...props
  }: TabsExtendsProps) => (
    <TabsMui
      {...props}
      onChange={onChange}
      TabIndicatorProps={{
        sx: {
          display: 'flex',
          justifyContent: 'center',
          height: '100%'
        },
        children: <span className="indicator-inner" />
      }}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      sx={{
        ...props.sx,
        ...(!hideBottomLine
          ? {
              borderBottom: '1px solid',
              borderColor: theme.palette.neutral[800],
              width: '100%'
            }
          : {})
      }}
    >
      {tabs.map((tab, i) => {
        const tabValue = typeof tab === 'string' ? i : tab.value
        const tabKey = tabValue.toString()

        const newQuery = {
          tab: tabKey,
          ...(tabKey === FeatureTabs.CHARGES && {
            due_date_between: getCurrentMonth().join('|')
          }),
          ...(tabKey === FeatureTabs.TRANSFERS && {
            transfer_date_between: getCurrentMonth().join('|')
          }),
          ...(tabKey === FeatureTabs.NFS && {
            created_at_between: getCurrentMonth().join('|')
          }),
          ...(tabKey === FeatureTabs.ACCOUNTS_PAYABLE && {
            due_date_between: getCurrentMonth().join('|')
          })
        }

        const href = `${window.location.pathname}?${new URLSearchParams(
          newQuery
        ).toString()}`

        return (
          <Tab
            fontSize={fontSize}
            label={
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <Link
                  href={href}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  onClick={(e) => {
                    e.preventDefault()
                    mixpanelTyped.__loaded &&
                      // SC-10694: `menu` era a string literal 'null' aqui, o que
                      // deixava 72% dos "Clique no menu" (54.757 de 75.554 em
                      // 30d) sem origem analisável. Tabs não pertencem a um
                      // menu do sidebar: omitir a propriedade é honesto, o
                      // `origem` já diferencia a fonte do clique.
                      mixpanel.track('Clique no menu', {
                        origem: 'tabs',
                        submenu: typeof tab === 'string' ? tab : tab.label
                      })
                    onChange(e, tabValue)
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3
                    }}
                  >
                    {typeof tab === 'string' ? tab : tab.label}
                    {typeof tab !== 'string' && tab.dot && (
                      <Icon
                        icon="error"
                        sx={{
                          color: theme.palette.error[300],
                          fontSize: '0.9rem'
                        }}
                      />
                    )}
                  </span>
                </Link>
                {typeof tab !== 'string' && tab.adornment
                  ? tab.adornment
                  : null}
              </span>
            }
            value={tabValue}
            key={i}
            sx={{ pb: 0 }}
          />
        )
      })}
    </TabsMui>
  )
)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: 'transparent',
    // o indicador tem height 100% e cobre a aba selecionada inteira; sem isso
    // ele intercepta o mouse e bloqueia hovers de elementos dentro da aba (ex.: tooltip)
    pointerEvents: 'none'
  },
  '& .indicator-inner': {
    width: '70%',
    height: '3px',
    borderRadius: '100px',
    backgroundColor: theme.palette.neutral[400],
    display: 'block',
    position: 'absolute',
    bottom: 0
  },
  '& .MuiTabs-scrollButtons.Mui-disabled ': {
    opacity: '0.3'
  },
  '& .MuiTabs-scrollButtons': {
    width: '30px'
  }
}))

export default Tabs
